import { useTimeoutFn } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import type { ComputedRef, Ref } from 'vue';
import type { ITicketError, Ticket } from '@/interface/ITicket';
import { PlaceOrderStatus, StatementDetailStatus } from '@/interface/enum';
import type {
    IPlaceTicketOrder,
    IPlaceTicketResult,
} from '@/libraries/apis/ticketApi';
import { placeTickets as placeTicketsApi } from '@/libraries/apis/ticketApi';
import { saveLastStake } from '@/libraries/betSlip/lastStakeStorage';
import type {
    IPlacedOrderErrorContext,
    ISingleModeFooterMessage,
} from '@/libraries/betSlip/singleModeBetSlipErrorMessage';
import {
    PlacedOrderFailureAction,
    buildGeneralFailureError,
    buildPlaceBetSummaryMessage,
    parseAlternateStake,
    resolvePlacedOrderFailurePolicy,
    toSingleModeFooterMessage,
} from '@/libraries/betSlip/singleModeBetSlipErrorMessage';
import { createPlaceTicketSnapshot } from '@/libraries/betSlip/ticketHelper';
import { useAutoClearError } from '@/libraries/composables/useAutoClearError';
import { platform } from '@/libraries/helpers/platformHelper';
import { getSportsBsiUrl } from '@/libraries/url';
import { sleep } from '@/libraries/utils/utils';
import { useAccountStore } from '@/stores/accountStore';
import { useBetCountStore } from '@/stores/betCountStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useRootStore } from '@/stores/rootStore';

const AFTER_PLACE_BET_REFRESH_DELAY_MS = 1000;

export interface ISingleModePlaceTicketDeps {
    placeableTickets: ComputedRef<Ticket[]>;
    allTickets: ComputedRef<Ticket[]>;
    hasTickets: ComputedRef<boolean>;
    isPlacingBet: ComputedRef<boolean>;
    getTicket: (key: string) => Ticket | undefined;
    removeTicket: (key: string) => void;
    removeAllTickets: () => void;
    updateTicketStake: (ticket: Ticket, stake: number) => void;
    refreshTicket: (key: string) => Promise<void>;
    refreshAllTickets: () => Promise<void>;
    closeStakePad: () => void;
    getEffectiveTicketStake: (ticket: Ticket) => number;
    getOrderVoucherId: (key: string) => string | null;
    isVoucherRedemptionEnabled: Ref<boolean>;
    placeBetFooterErrorDelayMs: number;
}

export interface ISingleModePlaceTicket {
    placeBetFooterError: Ref<ISingleModeFooterMessage | null>;
    placeSingleTabTickets: () => Promise<void>;
    showPlaceBetFooterError: (error: ITicketError) => void;
    clearPlaceBetFooterError: () => void;
}

// The explicit return type breaks the type-inference cycle between the store
// and this composable (the injected removeAllTickets calls
// clearPlaceBetFooterError from the returned object).
export function useSingleModePlaceTicket(deps: ISingleModePlaceTicketDeps): ISingleModePlaceTicket {
    const {
        placeableTickets,
        allTickets,
        hasTickets,
        isPlacingBet,
        getTicket,
        removeTicket,
        removeAllTickets,
        updateTicketStake,
        refreshTicket,
        refreshAllTickets,
        closeStakePad,
        getEffectiveTicketStake,
        getOrderVoucherId,
        isVoucherRedemptionEnabled,
        placeBetFooterErrorDelayMs,
    } = deps;

    const rootStore = useRootStore();
    const {
        setTopScreenToast,
        setShowAcceptAnyOddsBanner,
        setFailedPlaceBetShowAaoBanner,
    } = rootStore;
    const { brandName } = storeToRefs(rootStore);
    const { refreshBetCount } = useBetCountStore();
    const { customerInfo } = storeToRefs(useAccountStore());
    const { formatMoney } = useAccountStore();
    const { placeBetDelayTime } = storeToRefs(useFeatureToggleStore());

    const {
        error: placeBetFooterError,
        showTemporary: showPlaceBetFooterMessage,
        clear: clearPlaceBetFooterError,
    } = useAutoClearError<ISingleModeFooterMessage>(placeBetFooterErrorDelayMs);
    const afterPlaceBetRefreshTimer = useTimeoutFn(() => {
        refreshBetCount(false);
        platform.updateBalance();
        window.eventEmitter.emit('refreshBetList');
    }, AFTER_PLACE_BET_REFRESH_DELAY_MS, { immediate: false });

    async function placeSingleTabTickets() {
        if (isPlacingBet.value) {
            return;
        }
        closeStakePad();
        const placingTickets = [...placeableTickets.value];
        if (placingTickets.length === 0) {
            return;
        }
        clearPlaceBetFooterError();
        const placingTicketsByKey = new Map(placingTickets.map(ticket => [ticket.key, ticket]));
        removeEmptyTickets(placingTicketsByKey);
        placingTickets.forEach((ticket) => {
            ticket.isPlacingBet = true;
        });

        try {
            const requestOrders = placingTickets.map(createPlaceTicketOrder);
            const results = await requestPlaceTicketResults(requestOrders);
            if (results === null) {
                return;
            }
            if (placingTickets.length === 1) {
                await applySinglePlaceResults(results, placingTicketsByKey);
            } else {
                await applyMultiPlaceResults(results, placingTicketsByKey);
            }
        } finally {
            placingTickets
                .filter(isCurrentPlacingTicket)
                .forEach((placingTicket) => {
                    placingTicket.isPlacingBet = false;
                });
        }
    }

    async function requestPlaceTicketResults(
        requestOrders: IPlaceTicketOrder[],
    ): Promise<IPlaceTicketResult[] | null> {
        try {
            const [results] = await Promise.all([
                placeTicketsApi(requestOrders),
                sleep(placeBetDelayTime.value),
            ]);
            return results;
        } catch (error) {
            console.error('Failed to place Single mode Tickets', error);
            setFailedPlaceBetShowAaoBanner(true);
            showPlaceBetFooterError(buildGeneralFailureError({
                message: '',
                placeOrderStatus: PlaceOrderStatus.GeneralFailure,
            }));
            return null;
        }
    }

    function removeEmptyTickets(
        placingTicketsByKey: ReadonlyMap<string, Ticket>,
    ) {
        allTickets.value
            .filter(ticket => ticket.stake === 0 && !placingTicketsByKey.has(ticket.key))
            .forEach(ticket => removeTicket(ticket.key));
    }

    function createPlaceTicketOrder(ticket: Ticket): IPlaceTicketOrder {
        const snapshot = createPlaceTicketSnapshot(ticket);
        snapshot.stake = getEffectiveTicketStake(ticket);
        return {
            ticket: snapshot,
            voucherId: getOrderVoucherId(ticket.key),
        };
    }

    async function applySinglePlaceResults(
        results: IPlaceTicketResult[],
        placingTicketsByKey: ReadonlyMap<string, Ticket>,
    ) {
        const result = results[0];
        const placingTicket = result ? placingTicketsByKey.get(result.key) : undefined;
        if (!result || !placingTicket || !isCurrentPlacingTicket(placingTicket)) {
            return;
        }
        if (result.isSuccess) {
            recordLastStake(placingTicket);
            removeTicket(result.key);
            handleAllSuccessOutcome([result]);
            if (hasTickets.value) {
                await refreshAllTickets();
            }
            return;
        }
        routeSingleFailedPlaceTicket(result, placingTicket);
    }

    function recordLastStake(placedTicket: Ticket) {
        if (isVoucherRedemptionEnabled.value) {
            return;
        }
        saveLastStake(placedTicket.stake);
    }

    function routeSingleFailedPlaceTicket(result: IPlaceTicketResult, placingTicket: Ticket) {
        setFailedPlaceBetShowAaoBanner(true);
        const { action, buildError } = resolvePlacedOrderFailurePolicy(result.placeOrderStatus);
        const error = buildError(result, placedOrderErrorContext());
        switch (action) {
            case PlacedOrderFailureAction.RedirectToBsi:
                redirectToSportsBsi();
                return;
            case PlacedOrderFailureAction.Reopen:
            case PlacedOrderFailureAction.RetryOpen:
                showPlaceBetFooterError(error);
                refreshTicket(placingTicket.key);
                return;
            case PlacedOrderFailureAction.Cancel:
                removeTicket(result.key);
                setTopScreenToast({
                    status: 'error',
                    ...toSingleModeFooterMessage(error, formatMoney),
                    messagePosition: 'position-bottom',
                });
                return;
            case PlacedOrderFailureAction.AdjustStake:
                adjustTicketStakeToAlternate(result, placingTicket);
                showPlaceBetFooterError(error);
                return;
            default:
                showPlaceBetFooterError(error);
        }
    }

    function adjustTicketStakeToAlternate(result: IPlaceTicketResult, placingTicket: Ticket) {
        const alternateStake = parseAlternateStake(result.extraInfo);
        if (alternateStake !== null) {
            updateTicketStake(placingTicket, alternateStake);
        }
    }

    async function applyMultiPlaceResults(
        results: IPlaceTicketResult[],
        placingTicketsByKey: ReadonlyMap<string, Ticket>,
    ) {
        const currentResults = results.filter((result) => {
            const placingTicket = placingTicketsByKey.get(result.key);
            return placingTicket !== undefined && isCurrentPlacingTicket(placingTicket);
        });
        if (currentResults.length === 0) {
            return;
        }
        const failedResults = currentResults.filter(result => !result.isSuccess);
        if (failedResults.length === 0) {
            currentResults.forEach(result => removeTicket(result.key));
            handleAllSuccessOutcome(currentResults);
            if (hasTickets.value) {
                await refreshAllTickets();
            }
            return;
        }
        applyMixedPlaceResults(currentResults, failedResults);
    }

    function applyMixedPlaceResults(
        currentResults: IPlaceTicketResult[],
        failedResults: IPlaceTicketResult[],
    ) {
        const isRedirectToBsi = failedResults.some(result => (
            resolvePlacedOrderFailurePolicy(result.placeOrderStatus).action === PlacedOrderFailureAction.RedirectToBsi
        ));
        if (isRedirectToBsi) {
            redirectToSportsBsi();
            return;
        }
        const succeededCount = currentResults.length - failedResults.length;
        const summaryMessage = buildPlaceBetSummaryMessage(succeededCount, failedResults.length);
        removeAllTickets();
        setTopScreenToast({
            ...summaryMessage,
            messagePosition: 'position-bottom',
        });
        setShowAcceptAnyOddsBanner(true);
        if (succeededCount > 0) {
            scheduleAfterPlaceBetRefresh();
        }
    }

    function handleAllSuccessOutcome(succeeded: IPlaceTicketResult[]) {
        const isAllConfirmed = succeeded.every(result => result.orderStatus === StatementDetailStatus.Running);
        setTopScreenToast({
            status: 'placeBetSuccess',
            messageKey: isAllConfirmed ? 'betSlip_placeBetConfirmed' : 'betSlip_placeBetBeingProcessed',
            messagePosition: 'position-bottom',
        });
        setShowAcceptAnyOddsBanner(true);
        scheduleAfterPlaceBetRefresh();
    }

    function scheduleAfterPlaceBetRefresh() {
        afterPlaceBetRefreshTimer.start();
    }

    function placedOrderErrorContext(): IPlacedOrderErrorContext {
        return {
            playerType: customerInfo.value.playerType,
            brandName: brandName.value,
        };
    }

    function redirectToSportsBsi() {
        window.location.href = getSportsBsiUrl();
    }

    function isCurrentPlacingTicket(placingTicket: Ticket): boolean {
        return getTicket(placingTicket.key) === placingTicket;
    }

    function showPlaceBetFooterError(error: ITicketError) {
        showPlaceBetFooterMessage(toSingleModeFooterMessage(error, formatMoney));
    }

    return {
        placeBetFooterError,
        placeSingleTabTickets,
        showPlaceBetFooterError,
        clearPlaceBetFooterError,
    };
}
